/*
  Cart drawer. Owner: K.

  Public contract (used by product-form.js, quick add and the shoppable-video player):
    getSectionsToRender(), renderContents(parsedState), setActiveElement(element),
    open(triggeredBy), close(), and the "active" class on <cart-drawer> (the player
    observes it to pause playback). "is-empty" marks the empty state.
*/
const CART_DRAWER_FOCUSABLE =
  "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^='-']), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])";

// Clear space kept between a focused control and whatever would hide it (the pinned block
// below, the edge of the list above): the 2px focus ring, its 2px offset, and a little air.
const CART_DRAWER_FOCUS_CLEARANCE = 8;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);

    // Delegated, so it keeps working after #CartDrawer is re-rendered. The overlay, the X,
    // and "Continue shopping" after the summary ([data-af-cart-close]) all close.
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay, .drawer__close, [data-af-cart-close]')) this.close();
    });

    // Once now, and once after the browser has scrolled the focused control into view
    // (it brings it to the bottom edge of the scroll area, which is under the pinned block).
    this.addEventListener('focusin', (event) => {
      const element = event.target;
      this.revealFocused(element);
      setTimeout(() => {
        if (document.activeElement === element) this.revealFocused(element);
      });
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    // On the cart page the icon keeps its link: the page itself is the cart.
    if (window.routes && window.location.pathname.replace(/\/$/, '') === window.routes.cart_url) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // The transition does not always start when the class is added in the same task.
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.updateCartUpsellToggleState();
        cartDrawerItems.updateCartUpsellVisibility();
      }
      this.focusDialog();
    });

    document.addEventListener('keydown', this.onDocumentKeyDown);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.removeEventListener('keydown', this.onDocumentKeyDown);
    removeTrapFocus();
    // Focus goes back to the control that opened the drawer, without scrolling to it: the
    // shopper stays where they were on the page. (The page could not scroll while the
    // drawer was open, so the opener is where it was when it was pressed. Left to scroll,
    // a focus on the cart icon in the sticky header pulled a scrolled page up by a screen.)
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      this.activeElement.focus({ preventScroll: true });
    }
    document.body.classList.remove('overflow-hidden');
  }

  /* Focus: move into the dialog on open, keep Tab inside it, Escape closes. The list of
     focusable elements is read at key time, so it is still right after a re-render. */
  getDialog() {
    return this.querySelector('.drawer__inner');
  }

  getFocusableElements() {
    const dialog = this.getDialog();
    if (!dialog) return [];
    return Array.from(dialog.querySelectorAll(CART_DRAWER_FOCUSABLE)).filter(
      (element) => element.getClientRects().length > 0
    );
  }

  focusDialog() {
    const move = () => {
      const dialog = this.getDialog();
      if (dialog && this.classList.contains('active') && !dialog.contains(document.activeElement)) {
        dialog.focus({ preventScroll: true });
      }
    };
    move();
    // Again on the next frame, in case the drawer was not yet focusable when the class was added.
    requestAnimationFrame(move);
  }

  /* The block pinned to the bottom of the scroll area, or null when nothing is pinned.
     assets/af-cart.css decides by the height of the screen: the offer bar with the summary
     (.af-cart-drawer__pinned), the summary alone, or nothing. Whichever it is has
     position: sticky and a box of its own. */
  getPinned() {
    const candidates = this.querySelectorAll('.af-cart-drawer__pinned, .af-cart-drawer .af-cart-summary');
    return (
      Array.from(candidates).find((candidate) => {
        const style = getComputedStyle(candidate);
        return style.position === 'sticky' && style.display !== 'contents';
      }) || null
    );
  }

  /* A control that holds keyboard focus in the scroll area must be in view (WCAG 2.4.11).
     Two things can hide it:
       - the pinned block (the offer bar, the totals, Checkout and the trust lines) stays
         at the bottom of the scroll area and covers the lines and the cross-sell behind
         it; only controls that come before it can be covered;
       - after a line is removed, focus moves by script to the line that took its place,
         and the list keeps its scroll position, so that line can sit above the top edge.
     The list scrolls until the control is clear. Focus taken with a pointer is left
     alone: the control is already under the finger, and moving it between press and
     release would lose the click. */
  revealFocused(element) {
    const scroller = this.querySelector('.af-cart-drawer__scroll');
    if (!scroller || !element || element === scroller || !scroller.contains(element)) return;
    try {
      if (!element.matches(':focus-visible')) return;
    } catch (error) {
      // A browser without :focus-visible: treat every focus as keyboard focus.
    }

    const target = element.getBoundingClientRect();
    const frame = scroller.getBoundingClientRect();

    const above = frame.top + CART_DRAWER_FOCUS_CLEARANCE - target.top;
    if (above > 0) {
      scroller.scrollTop -= above;
      return;
    }

    const pinned = this.getPinned();
    if (!pinned || pinned.contains(element)) return;

    const cover = pinned.getBoundingClientRect();
    const overlap = target.bottom + CART_DRAWER_FOCUS_CLEARANCE - cover.top;
    if (overlap > 0 && target.top < cover.bottom) scroller.scrollTop += overlap;
  }

  onDocumentKeyDown(event) {
    if (!this.classList.contains('active')) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const dialog = this.getDialog();
    const focusable = this.getFocusableElements();
    if (!dialog || focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;

    if (!dialog.contains(current)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && (current === first || current === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* WCAG 4.1.3: say what a change did. The status region comes with the drawer's markup,
     empty, and carries its sentence in data-af-message ("New estimated total: ...", or
     "Your cart is empty"; printed by snippets/cart-drawer.liquid). It is written a moment
     later, because assistive technology announces a change inside a region it already
     knows, not a region that arrives with its text. */
  announce() {
    const status = this.querySelector('#CartDrawer-LiveRegionText');
    if (!status || !status.dataset.afMessage) return;
    setTimeout(() => {
      // A newer change has replaced the region: that one speaks.
      if (status.isConnected) status.textContent = status.dataset.afMessage;
    }, 200);
  }

  renderContents(parsedState) {
    // Open already: the add came from inside the drawer (the cross-sell) or from the video
    // player behind it. Opening a closed drawer moves focus into the dialog, which is
    // announced by itself; an open one changes in place, so the new total is said.
    const wasOpen = this.classList.contains('active');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Something was just added: the drawer now holds a line item.
    if (this.querySelector('.cart-item')) this.classList.remove('is-empty');

    if (wasOpen) this.announce();

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/*
  Line items in the drawer. assets/cart.js (CartItems) does the work; after every quantity
  change or removal it replaces the whole of .drawer__inner, scroll area included, and then
  moves focus. Left alone, that sends the list back to the top, and when the pressed button
  did not hold focus (iOS Safari does not focus a button on tap) it puts focus on the first
  line. This subclass keeps the shopper on the line they changed:
    - the stepper button that was pressed is remembered, so focus can return to it even
      when the browser never focused it;
    - the scroll position is read just before the markup is replaced and put back once
      cart.js has finished with the new markup;
    - after a removal, focus goes to the line that took the removed line's place, and the
      list scrolls if that line is above its top edge or behind the pinned block
      (CartDrawer.revealFocused).
*/
class CartDrawerItems extends CartItems {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      // Buttons only: returning focus to the quantity field would reopen a phone's keyboard.
      const control = event.target.closest('button[name]');
      const lineItem = control && control.closest('.cart-item');
      const indexed = lineItem && lineItem.querySelector('[data-index]');
      this.lastPressed = indexed ? { line: indexed.dataset.index, name: control.getAttribute('name') } : null;
    });
  }

  updateQuantity(line, quantity, name, variantId) {
    const pressed = this.lastPressed;
    this.lastPressed = null;
    if (!name && pressed && String(pressed.line) === String(line)) name = pressed.name;

    this.pendingChange = { line: parseInt(line, 10), removed: parseInt(quantity, 10) === 0 };
    return super.updateQuantity(line, quantity, name, variantId);
  }

  // cart.js calls this for each section right before it replaces that section's markup.
  getSectionInnerHTML(html, selector) {
    if (selector === '.drawer__inner') {
      const scroller = document.querySelector('cart-drawer .af-cart-drawer__scroll');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const change = this.pendingChange;
      this.pendingChange = null;
      // A microtask runs when cart.js has finished its own handling of the response
      // (new markup in place, its focus call made), and before the browser paints.
      Promise.resolve().then(() => this.afterRender(scrollTop, change));
    }
    return super.getSectionInnerHTML(html, selector);
  }

  afterRender(scrollTop, change) {
    const drawer = document.querySelector('cart-drawer');
    // The new total (or "Your cart is empty"), for assistive technology: CartDrawer.announce.
    if (drawer && typeof drawer.announce === 'function') drawer.announce();

    const scroller = drawer && drawer.querySelector('.af-cart-drawer__scroll');
    if (!scroller) return;

    // Back to where the shopper was, before any focus call (focus only scrolls when its
    // target is out of view).
    scroller.scrollTop = scrollTop;

    const lines = drawer.querySelectorAll('#CartDrawer-CartItems .cart-item');
    const dialog = drawer.querySelector('.drawer__inner');
    const focused = document.activeElement;
    const focusIsOnALineControl =
      !!focused && !!focused.closest('#CartDrawer-CartItems') && !focused.matches('.cart-item__name');

    if (lines.length && !focusIsOnALineControl) {
      if (change && change.removed) {
        // cart.js went to the first line; go to the one now where the removed line was.
        const nearest = lines[Math.min(change.line, lines.length) - 1];
        const target = nearest && nearest.querySelector('.cart-item__name');
        if (target) target.focus();
      } else if (dialog && drawer.classList.contains('active')) {
        // No control to return to: stay in the dialog rather than jump to the first line.
        dialog.focus({ preventScroll: true });
      }
    }

    if (typeof drawer.revealFocused === 'function') drawer.revealFocused(document.activeElement);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
